import {
  adminV2Challenges,
  adminV2ReceiptItems,
  adminV2Receipts,
} from "@api/all";
import { useSWRDataTable } from "@bleu.builders/ui";
import { useProgram } from "@contexts/ProgramContext";
import { client } from "@utils/api-client";
import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import useSWR from "swr";

export const fetcher = async ([url]) => {
  const response = await client(url, {
    headers: {
      Accept: "application/json",
    },
  });

  return response;
};

interface Receipt {
  id: number;
  participant_id: number;
  participant_points: number;
  participant_completed_challenges: number;
  admin_invalidation_message?: string;
  admin_invalidation_reason?: string;
  admin_marked_as_valid_at?: string;
  admin_marked_as_invalid_at?: string;
  program_id: number;
  status: string;
  verified_at: string;
  created_at: string;
  updated_at: string;
  source: "perk" | "external";
  points: number;
  total_amount: number;
  processed_data: object;
  merchant_name: string;
  merchant_address: string;
  images: string[];
  items_count: number;
  open_receipt_issue_reports?: string[];
  receipt_tags: {
    id: number;
    name: string;
    reason: string;
    linked_data: object;
  }[];
}

interface Product {
  id: number;
  points_per_cents: number;
  product_name: string;
  product_description: string;
  product_active: boolean;
}

interface ReceiptUpdateContextProps {
  editingItems: Array<string | number>;
  setEditingItems: React.Dispatch<React.SetStateAction<Array<string | number>>>;
  selectedRows: Array<string | number>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Array<string | number>>>;
  receipt_id?: string;
  setNonSavedUpdates: React.Dispatch<React.SetStateAction<string[]>>;
  nonSavedUpdates: string[];
  receipt: Receipt;
  products: Product[];
  isLoading: boolean;
  form: ReturnType<typeof useForm>;
  rejectReasonsOptions: { label: string; value: string }[];
  merchantOptions: { label: string; value: string }[];
  fieldArray: ReturnType<typeof useFieldArray>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  isLoadingReceipt: boolean;
  isLoadingReceiptError: boolean;
  error: {
    message: string | null;
  } | null;
}

interface ReceiptUpdateProviderProps {
  children: ReactNode;
  receiptId: string;
}

const ReceiptUpdateContext = createContext<ReceiptUpdateContextProps | null>(
  null,
);

export const ReceiptUpdateProvider: React.FC<ReceiptUpdateProviderProps> = ({
  children,
  receiptId,
}) => {
  const [nonSavedUpdates, setNonSavedUpdates] = React.useState([]);
  const [editingItems, setEditingItems] = useState<Array<string | number>>([]);
  const [selectedRows, setSelectedRows] = useState<Array<string | number>>([]);
  const { receipt_id } = useParams<{ receipt_id: string }>();

  const program = useProgram();

  const {
    data: receiptData,
    error: receiptError,
    isLoading: receiptLoading,
  } = useSWR(
    [
      adminV2Receipts.show.path({
        id: receiptId,
      }),
    ],
    { fetcher, revalidateOnFocus: false },
  );

  const {
    data: productsData,
    error: productsError,
    isLoading: productsLoading,
  } = useSWR(
    [
      adminV2Challenges.purchaseProofEligibleProducts.path({
        program_id: program.id,
      }),
      {},
    ],
    { fetcher, revalidateOnFocus: false },
  );

  const { data, isLoading, error } = useSWRDataTable(
    adminV2ReceiptItems.index.path({ receipt_id: receipt_id }),
    {
      sorting: [{ id: "created_at", desc: false }],
      pagination: {
        pageIndex: 0,
        pageSize: 1000000,
      },
    },
    { revalidateOnFocus: false },
  );

  const isLoadingReceipt = receiptLoading || productsLoading || isLoading;
  const isLoadingReceiptError = receiptError || productsError || error;

  const form = useForm({});

  const { control, reset } = form;

  useEffect(() => {
    if (!data?.data) return;
    if (!receiptData) return;

    const receipt = receiptData.receipt;

    if (data?.data) {
      reset({
        status: receipt["status"],
        merchant_name: receipt["merchant_name"],
        admin_invalidation_reason: receipt["admin_invalidation_reason"],
        admin_invalidation_message: receipt["admin_invalidation_message"],
        integrity: receipt["status"] === "rejected" ? "invalid" : "valid",
        purchase_proof_merchant_id: receipt["purchase_proof_merchant_id"],
        merchant_address: receipt["merchant_address"],
        admin_comments: receipt["admin_comments"],
        purchase_date: receipt["purchase_date"],
        total_amount: receipt["total_amount"],
        items_count: receipt["items_count"],
        order_id: receipt["id"],
        receipt_items: data.data.map((item) => ({
          ...item,
          patch_status: "unchanged",
        })),
      });
    }
  }, [data, reset, receipt_id, receiptData]);

  const fieldArray = useFieldArray({
    control,
    name: "receipt_items",
    keyName: "identifier",
  });

  return (
    <ReceiptUpdateContext.Provider
      value={{
        editingItems,
        setEditingItems,
        selectedRows,
        setSelectedRows,
        receipt_id,
        nonSavedUpdates,
        setNonSavedUpdates,
        receipt: receiptData?.receipt,
        products: productsData || [],
        rejectReasonsOptions: receiptData?.reject_reasons_options || [],
        merchantOptions: receiptData?.merchant_options || [],
        isLoading,
        data,
        form,
        fieldArray,
        isLoadingReceipt,
        isLoadingReceiptError,
        error,
      }}
    >
      {children}
    </ReceiptUpdateContext.Provider>
  );
};

export const useReceiptUpdate = (): ReceiptUpdateContextProps => {
  const context = useContext(ReceiptUpdateContext);
  if (!context) {
    throw new Error(
      "useReceiptUpdate must be used within a ReceiptUpdateProvider",
    );
  }
  return context;
};
